/**
 * UploadedStatementsPanel.jsx — Phase 84.18
 *
 * Always-visible list on /import that shows every statement upload the
 * user has ever made. Bell dropdown = ephemeral (recent-only, popover);
 * this panel = permanent record.
 *
 * Row states:
 *   • held      — status ∈ {done, parsed, needs_review} AND not consumed_at
 *                 → active "Review & Import" CTA
 *   • imported  — consumed_at present AND NOT purged
 *                 → secondary "View in transactions" CTA
 *   • purged    — is_purged=true (server tells us the PDF is gone; retention
 *                 sweeper runs 24h after consumption per storage_lifecycle.py)
 *                 → disabled button with a plain-English retention message.
 *   • active    — queued / processing / retrying
 *                 → non-actionable status pill only
 *   • failed    — status='failed'
 *                 → error message + dismiss button
 *
 * The panel header shows "(N held)" — the count of actionable
 * held-for-review rows. When 0, we say "(0 held)" so the user gets
 * closure that nothing is waiting on them.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, ArrowRight, Trash2,
  ShieldCheck, RefreshCw, Inbox,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useParseJobs } from '@/hooks/useParseJobs';
import { jobState, statusPill, importedLabel, readCount, metaLine } from '@/lib/importStatus';

// Status vocabulary lives in lib/importStatus.js so the bell and this panel
// can never disagree about what `committed` / `completed` mean.

function relTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  return `${mo}mo ago`;
}

function StatusPill({ job }) {
  const m = statusPill(job);
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${m.cls}`}>
      <Icon className={`h-3 w-3 ${m.spin ? 'animate-spin' : ''}`} /> {m.label}
    </span>
  );
}

/**
 * Compute the row state used by the UI.
 *  - 'held'     → user can Review & Import
 *  - 'imported' → committed, file still there
 *  - 'purged'   → committed, file removed (24h+ ago)
 *  - 'active'   → still parsing
 *  - 'failed'   → parse failed
 */
function deriveRowState(job) {
  return jobState(job);
}

export default function UploadedStatementsPanel() {
  const navigate = useNavigate();
  // Larger limit so this panel functions as a real history view. Bell
  // continues to use the default (20).
  const { jobs, refresh, deleteJob } = useParseJobs({ noPoll: true, limit: 100 });

  const { heldCount, rows } = useMemo(() => {
    const enriched = (jobs || []).map((j) => ({ ...j, _state: deriveRowState(j) }));
    return {
      heldCount: enriched.filter((j) => j._state === 'held').length,
      rows: enriched,
    };
  }, [jobs]);

  const handleReview = (job) => {
    navigate(`/import?job=${job.id}`);
  };

  return (
    <Card className="rounded-2xl" data-testid="uploaded-statements-panel">
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Inbox className="h-4 w-4 text-primary" />
            Uploaded Statements
            <Badge
              variant="outline"
              className="rounded-full text-[10px] font-normal"
              data-testid="uploaded-statements-held-count"
            >
              {heldCount} held
            </Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Every statement you&rsquo;ve uploaded. Original PDFs are removed from our platform
            ~24 hours after import for your privacy — the transactions stay in your ledger.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-lg h-8"
          onClick={() => refresh()}
          data-testid="uploaded-statements-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="uploaded-statements-empty">
            You haven&rsquo;t uploaded any statements yet.
            <div className="text-xs mt-1 opacity-80">
              Drop a PDF, CSV, OFX or XLSX above to get started.
            </div>
          </div>
        ) : (
          <TooltipProvider delayDuration={150}>
            <ul className="divide-y divide-border/60">
              {rows.map((job) => (
                <UploadedRow
                  key={job.id}
                  job={job}
                  onReview={handleReview}
                  onDelete={deleteJob}
                />
              ))}
            </ul>
          </TooltipProvider>
        )}
      </CardContent>
    </Card>
  );
}

function UploadedRow({ job, onReview, onDelete }) {
  const state = job._state;
  const isActive = state === 'active';
  const isFailed = state === 'failed';
  const isPurged = state === 'purged';
  const isImported = state === 'imported';
  const isHeld = state === 'held';
  const count = isHeld ? readCount(job) : 0;

  return (
    <li
      className="px-5 py-3.5 hover:bg-accent/20 transition-colors"
      data-testid={`uploaded-row-${job.id}`}
      data-row-state={state}
    >
      <div className="flex items-start gap-3">
        <FileText className={`h-4 w-4 mt-0.5 shrink-0 ${isPurged ? 'text-muted-foreground/50' : 'text-muted-foreground'}`} />

        <div className="flex-1 min-w-0">
          {/* Filename + status */}
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className={`text-sm font-medium truncate max-w-[220px] sm:max-w-[320px] ${isPurged ? 'text-muted-foreground' : ''}`}
              title={job.filename}
            >
              {job.filename}
            </div>
            <StatusPill job={job} />
          </div>

          {/* Secondary meta line */}
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {isHeld && metaLine(`Read ${count} transaction${count === 1 ? '' : 's'}`, relTime(job.finished_at))}
            {isImported && metaLine(importedLabel(job), relTime(job.consumed_at || job.finished_at))}
            {isPurged && metaLine(importedLabel(job), relTime(job.consumed_at || job.finished_at))}
            {isActive && `Started ${relTime(job.started_at || job.created_at)}`}
            {isFailed && (
              <span className="text-destructive break-words" title={job.error}>
                {job.error || 'Failed to parse'}
              </span>
            )}
          </div>

          {/* Action row */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {isHeld && (
              <Button
                size="sm"
                className="rounded-xl h-7 px-2.5"
                onClick={() => onReview(job)}
                data-testid={`uploaded-review-${job.id}`}
              >
                Review &amp; Import <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            )}
            {isImported && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  className="rounded-xl h-7 px-2.5"
                  onClick={() => (window.location.href = '/transactions')}
                  data-testid={`uploaded-view-${job.id}`}
                >
                  View in transactions <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </>
            )}
            {isPurged && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="rounded-xl h-7 px-2.5 opacity-60 pointer-events-none"
                      disabled
                      data-testid={`uploaded-purged-${job.id}`}
                    >
                      <ShieldCheck className="h-3 w-3 mr-1" /> File removed
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[280px] text-xs">
                  This statement has been imported into your ledger. The original file has been
                  removed from our platform for your privacy (retention: ~24 hours after import).
                  Your transactions remain available.
                </TooltipContent>
              </Tooltip>
            )}
            {isFailed && (
              <Button
                size="sm"
                variant="ghost"
                className="rounded-xl h-7 px-2.5 text-muted-foreground"
                onClick={() => onDelete(job.id)}
                data-testid={`uploaded-dismiss-${job.id}`}
              >
                <Trash2 className="h-3 w-3 mr-1" /> Dismiss
              </Button>
            )}
          </div>
        </div>

        {/* Right-hand dismiss for non-active rows that still have a "trash" affordance */}
        {!isActive && !isFailed && (
          <button
            type="button"
            onClick={() => onDelete(job.id)}
            className="text-muted-foreground/60 hover:text-foreground p-1 rounded-md"
            aria-label="Remove from history"
            data-testid={`uploaded-remove-${job.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </li>
  );
}
